import { useState, useEffect } from 'react';
import type { Filters } from './types';

type UseCasinoFilters = (initialFilters: Filters) => {
  filters: Filters;
  // Changes several filters at once, e.g. a whole menu list off the tag of the former one:
  // setFilter({ list: 'top', tag: '' }).
  setFilter: (changes: Filters) => void;
  // Clears every filter, the sidebar's list included: back to all games at `/`.
  reset: () => void;
  // Bumped on every reset: the name field drops its typed name then (NameFilter).
  resets: number;
};

const FILTER_NAMES: (keyof Filters)[] = ['provider', 'list', 'tag', 'name'];
const NO_FILTERS: Filters = { provider: '', list: '', tag: '', name: '' };

// eslint-disable-next-line max-lines-per-function
const useCasinoFilters: UseCasinoFilters = (initialFilters) => {
  const [filters, setFilters] = useState<Filters>(
    Object.fromEntries(Object.entries(initialFilters).filter(([key]) => FILTER_NAMES.includes(key as keyof Filters)))
  );
  const [resets, setResets] = useState(0);

  const submit = (newFilters: Filters = filters) => {
    const event = new CustomEvent('casinoSearch', { detail: newFilters });
    document.dispatchEvent(event);
  };

  const setFilter = (changes: Filters) => {
    setFilters((prev) => {
      const newFilters = { ...prev, ...changes };
      submit(newFilters);
      return newFilters;
    });
  };

  useEffect(() => {
    const synchronizeExternalFilters = (e: Event) => {
      setFilters((prev: Filters) => ({ ...prev, ...(e as CustomEvent<Filters>).detail }));
    };

    document.addEventListener('casinoSearch', synchronizeExternalFilters);
    return () => {
      document.removeEventListener('casinoSearch', synchronizeExternalFilters);
    };
  }, []);

  // Set here and not only through casinoSearch above: React renders an update made in a custom
  // event's listener later than one made in the click that resets, so the name field
  // (NameFilter, on its `resets`) would take the old name back.
  const reset = () => {
    setFilters(NO_FILTERS);
    setResets((n) => n + 1);
    submit(NO_FILTERS);
  };

  return {
    filters,
    setFilter,
    reset,
    resets,
  };
};

export default useCasinoFilters;
